import { StorageError } from '../../app/errors';
import type { ArtifactPhase, MatchNoteRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';
import type { MinuteRederivation } from './observations';

/**
 * `matchNotes` — INV-14's table.
 *
 * *"`MatchNote` is not an `Observation` and MUST NOT be merged into it: notes are mutable, and
 * folding them into the append-only ledger would weaken INV-1."*
 *
 * The separation is why this module can offer a plain `updateNote` while `repos/observations.ts`
 * offers nothing of the kind. INV-14's other half — *"never affects `cachedCount`, `status`, or any
 * derived value"* — is enforced one layer up: `domain/counts.replay` and `reconcile` take
 * `Observation[]` as their only row parameter, so a `MatchNoteRow` cannot be passed to them. It
 * appears in the cascade (INV-8) and in archives (SYN-4), and nowhere else.
 *
 * `rederiveMinutes` exists here as well as on observations because SES-8's boundary adjustment
 * re-derives *every* Observation **and MatchNote** minute in one transaction (§17.1). A note captured
 * at halftime carries `matchMinute = halfLengthMin` (SES-10), which moves when the half length or
 * the anchors move.
 */

/** @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure. */
export async function insertNote(row: MatchNoteRow): Promise<void> {
  requireTransaction('insertNote');
  assertMinute(row.matchMinute, row.id);
  try {
    await db().matchNotes.add(row);
  } catch (cause) {
    throw new StorageError('Could not save the note.', {
      cause,
      context: { store: TABLE.matchNotes, id: row.id, matchId: row.matchId },
    });
  }
}

/**
 * Edits a note. Mutable by design (INV-14) — but `matchMinute` is **not** in the patch type.
 *
 * A minute is derived from anchors by one pure function (INV-10) and moves only through SES-8's
 * re-derivation. Letting an edit carry a minute would give the product a second way to set one, and
 * INV-7 would then hold only by convention.
 *
 * @throws {StorageError} outside a transaction, if the note is missing, or on a write failure.
 */
export async function updateNote(
  id: string,
  changes: Partial<Pick<MatchNoteRow, 'text' | 'audioBlobId'>>,
  updatedAt: number,
): Promise<void> {
  requireTransaction('updateNote');
  const row = await db().matchNotes.get(id);
  if (!row) {
    throw new StorageError(`Note ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.matchNotes, id },
    });
  }
  try {
    await db().matchNotes.put({ ...row, ...changes, updatedAt });
  } catch (cause) {
    throw new StorageError('Could not update the note.', {
      cause,
      context: { store: TABLE.matchNotes, id },
    });
  }
}

/**
 * SES-8 — rewrites `matchMinute` and nothing else. Same parameter type as the observation path, so
 * the two halves of one adjustment cannot drift apart.
 *
 * @returns how many rows were rewritten.
 * @throws {StorageError} outside a transaction, on a missing row, on an invalid minute, or on a
 *   write failure.
 */
export async function rederiveMinutes(updates: readonly MinuteRederivation[]): Promise<number> {
  requireTransaction('rederiveMinutes');
  const table = db().matchNotes;
  let rewritten = 0;
  for (const update of updates) {
    assertMinute(update.matchMinute, update.id);
    const row = await table.get(update.id);
    if (!row) {
      throw new StorageError(
        `SES-8: note ${update.id} vanished mid-re-derivation; the adjustment was rolled back.`,
        { context: { store: TABLE.matchNotes, id: update.id } },
      );
    }
    if (row.matchMinute === update.matchMinute) continue;
    await table.put({ ...row, matchMinute: update.matchMinute });
    rewritten += 1;
  }
  return rewritten;
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteNotesForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteNotesForCascade');
  if (ids.length === 0) return;
  try {
    await db().matchNotes.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the notes during a cascade.', {
      cause,
      context: { store: TABLE.matchNotes, count: ids.length },
    });
  }
}

/**
 * One note by id, for `WriteMatchNote`'s snapshot (HT-8).
 *
 * INV-14 makes the row mutable, so "does this note already exist" is the question that decides
 * insert-vs-update — and §1.1 requires it be asked **inside** the transaction. Answering it at the
 * call site instead would race the user's own second save and surface as a duplicate-key abort.
 *
 * @throws {StorageError} on a read failure.
 */
export async function getNote(id: string): Promise<MatchNoteRow | undefined> {
  try {
    return await db().matchNotes.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the note.', {
      cause,
      context: { store: TABLE.matchNotes, id },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function listForMatch(
  matchId: string,
  phase?: ArtifactPhase,
): Promise<MatchNoteRow[]> {
  try {
    const rows = await db().matchNotes.where('matchId').equals(matchId).toArray();
    return rows
      .filter((row) => phase === undefined || row.phase === phase)
      .sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  } catch (cause) {
    throw new StorageError('Could not read the match’s notes.', {
      cause,
      context: { store: TABLE.matchNotes, matchId, phase },
    });
  }
}

function assertMinute(matchMinute: number, id: string): void {
  if (Number.isInteger(matchMinute) && matchMinute >= 0) return;
  throw new StorageError(
    `A note needs a finite non-negative integer matchMinute; ${id} carried ${String(matchMinute)}.`,
    { context: { store: TABLE.matchNotes, id, matchMinute } },
  );
}
